import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17191 — one storefront visit: an unbroken run of a portal user's
 * storefront activity on one storefront for one customer account, ending
 * after 30 minutes without a request or a "still here" heartbeat.
 *
 * Rolled up from the activity the storefront already generates (the
 * access-log writes plus the heartbeat), buffered in Redis and flushed by the
 * storefront-activity drain, so every engagement surface reads this table and
 * never scans `access_logs`.
 *
 * Not audited: it is written about once a minute per active shopper and is
 * derived data that can be rebuilt from `access_logs`.
 *
 * SM1 exposure: none. New table, never named by the legacy CRM.
 */
@Index('idx_storefront_visit_user_last_seen', [
	'fkPublicUserId',
	'lastSeenDate',
])
@Index('idx_storefront_visit_client_last_seen', ['fkClientId', 'lastSeenDate'])
@Index('idx_storefront_visit_last_seen', ['lastSeenDate'])
@Entity('storefront_visit', {}, { auditable: false })
export class StorefrontVisitEntity extends BaseEntity {
	@Column('int', { name: 'fk_public_user__id', unsigned: true })
	public fkPublicUserId!: number;

	/** The customer account the shopper was acting for; null if none was selected yet. */
	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public fkClientId!: number | null;

	/** The storefront (portal site) the requests came through. */
	@Column('int', { name: 'fk_portal_site__id', unsigned: true, nullable: true })
	public fkPortalSiteId!: number | null;

	@Column('datetime', { name: 'startedDate' })
	public startedDate!: Date;

	@Column('datetime', { name: 'lastSeenDate' })
	public lastSeenDate!: Date;

	/** Logged storefront requests in the visit (heartbeats excluded). */
	@Column('int', { name: 'actionCount', unsigned: true, default: 0 })
	public actionCount!: number;

	@Column('int', { name: 'searchCount', unsigned: true, default: 0 })
	public searchCount!: number;

	@Column('int', { name: 'cartAddCount', unsigned: true, default: 0 })
	public cartAddCount!: number;

	/** Orders the shopper submitted during the visit. */
	@Column('int', { name: 'orderCount', unsigned: true, default: 0 })
	public orderCount!: number;

	@Column('decimal', {
		name: 'orderTotal',
		precision: 13,
		scale: 2,
		default: 0,
	})
	public orderTotal!: string;

	@Column('int', { name: 'paymentCount', unsigned: true, default: 0 })
	public paymentCount!: number;

	@Column('decimal', {
		name: 'paymentTotal',
		precision: 13,
		scale: 2,
		default: 0,
	})
	public paymentTotal!: string;

	/** The storefront route of the latest request or heartbeat ("Viewing: Cart"). */
	@Column('varchar', { name: 'lastPath', length: 255, nullable: true })
	public lastPath!: string | null;

	@Column('varchar', { name: 'userAgent', length: 512, nullable: true })
	public userAgent!: string | null;

	@Column('varchar', { name: 'ipAddress', length: 45, nullable: true })
	public ipAddress!: string | null;

	/** True for visits rebuilt from historical access logs (no heartbeat, so reading time undercounts). */
	@Column('tinyint', { name: 'isBackfilled', width: 1, default: 0 })
	public isBackfilled!: boolean;
}
